// Identity fixtures: the demo member's own record — auth session, vault (person, akas,
// addresses, phones, emails), MFA, passkeys, login activity, protection options, marketing
// opt-outs, communication preferences, connected AI agents, family/contacts, and LPoA. Every
// mutation is applied to the in-memory state below so a save-then-refetch inside one demo
// session reads back the change — nothing crosses the network and nothing survives a reload.

import type { BaseAPIResponse } from '~/types/api';
import type { AuthUser } from '~/types/auth/auth';
import type { Address, Aka, Email, License, Person, PhoneNumber } from '~/types/person/person';
import type { QRData } from '~/types/person/settings';
import type { Contact, ContactEmail, ContactPhoneNumber, MinorEligibility } from '~/types/person/contacts';
import type {
    MarketingOptOutCoverage,
    MarketingOptOutResult,
    MarketingOptOutRow,
    MarketingOptOutsUpdatePayload,
} from '~/types/person/marketing-optouts';
import type {
    CommunicationPreferenceKey,
    PersonCommunicationPreferences,
    PersonCommunicationPreferencesUpdatePayload,
} from '~/services/person-communication-preferences-service';
import type { PersonOption, PersonOptionGroups, PersonOptions, PersonOptionsUpdatePayload } from '~/services/person-options-service';
import type { Passkey, PasskeyRegistrationOptions } from '~/services/passkey-service';
import type { LoginSession } from '~/services/login-activity-service';
import type { ConnectedAgent } from '~/services/connections-service';
import { primaryPaymentMethod } from './billing';
import { LicenseRenewalTerm, LicenseType } from '~/types/licenses/licenses';
import type { DemoRoute } from '../types';
import { envelope } from '../envelope';
import { isDemoPlanLocked } from '../mode';
import { DEMO_PERSON_ID, DEMO_PERSONA, demoDaysAgo, demoDaysAhead } from '../persona';
import { demoSponsoredSeat } from './license-gifts';

// ---------------------------------------------------------------------------------------------
// Mutable session state. A demo session is a live account, not a slideshow: adding an alias,
// toggling a monitoring option, or opting out of a marketing list has to still be there the next
// time the page reads it back. Everything below resets on reload — that's the whole point.
// ---------------------------------------------------------------------------------------------

let personCore = {
    first_name: DEMO_PERSONA.first_name,
    middle_name: 'Gary',
    last_name: DEMO_PERSONA.last_name,
    dob: DEMO_PERSONA.dob,
};

let poaSigned = true;
let poaSignedAt: string | undefined = demoDaysAgo(479);
const poaEnvelopeId = 'demo-envelope-903117';

let mfaEnabled = false;
const mfaSetupKey = 'DEMO2FASECRETKEY234567';
// A minimal, valid, static QR module grid — enough to render as a scannable-looking code in the
// MFA setup screen without hand-authoring or hashing a real TOTP payload.
const DEMO_QR_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 29" width="200" height="200" shape-rendering="crispEdges">' +
    '<rect width="29" height="29" fill="#fff"/>' +
    '<g fill="#000">' +
    '<path d="M0 0h7v7H0zM2 2h3v3H2zM22 0h7v7h-7zM24 2h3v3h-3zM0 22h7v7H0zM2 24h3v3H2z"/>' +
    '<path d="M9 0h1v1H9zM11 0h2v1h-2zM15 0h1v2h-1zM17 0h1v1h-1zM9 3h2v1H9zM13 3h1v2h-1zM16 3h2v1h-2zM9 5h1v2H9zM12 5h1v1h-1zM18 5h1v2h-1zM10 8h2v1h-2zM14 8h3v1h-3zM19 8h1v1h-1zM9 10h1v3H9zM11 10h1v1h-1zM15 10h1v3h-1zM17 10h2v1h-2zM9 14h3v1H9zM13 14h1v1h-1zM16 14h1v3h-1zM19 14h1v3h-1zM9 17h1v1H9zM11 17h2v1h-2zM14 17h1v1h-1zM9 19h2v2H9zM12 19h1v2h-1zM17 19h1v2h-1zM0 9h1v7H0zM2 9h2v1H2zM5 9h2v3H5zM1 12h1v3H1zM3 12h1v1H3zM0 17h1v3H0zM3 16h2v2H3zM6 17h1v3H6zM9 22h1v1H9zM11 22h1v3h-1zM14 22h2v1h-2zM17 22h1v1h-1zM19 22h2v1h-2zM9 24h3v1H9zM13 24h1v3h-1zM16 24h1v1h-1zM18 24h2v3h-2zM9 26h1v3H9zM11 27h1v1h-1z"/>' +
    '</g></svg>';

let akaIdSeq = 4;
let akas: Aka[] = [
    { id: 1, first_name: 'Mike', middle_name: null, last_name: 'Scott' },
    { id: 2, first_name: 'Michael', middle_name: 'G', last_name: 'Scott' },
    { id: 3, first_name: 'Michael', middle_name: 'Gary', last_name: 'Scott' },
];

let addressIdSeq = 3;
let addresses: Address[] = [
    {
        id: 1,
        primary: true,
        line_1: DEMO_PERSONA.address.line_1,
        line_2: DEMO_PERSONA.address.line_2,
        city: DEMO_PERSONA.address.city,
        region: DEMO_PERSONA.address.region,
        postal_code: DEMO_PERSONA.address.postal_code,
        country: DEMO_PERSONA.address.country,
    },
    {
        id: 2,
        primary: false,
        line_1: '1225 Carbondale Road',
        line_2: 'Apt 4B',
        city: 'Scranton',
        region: 'PA',
        postal_code: '18509',
        country: 'US',
    },
];

let phoneIdSeq = 3;
let phones: PhoneNumber[] = [
    { id: 1, primary: true, country_code: '1', phone_number: '5705550142', phone_full: '+15705550142' },
    { id: 2, primary: false, country_code: '1', phone_number: '5705550198', phone_full: '+15705550198' },
];

let emailIdSeq = 4;
let emails: Email[] = [
    { id: 1, primary: true, email: DEMO_PERSONA.email },
    { id: 2, primary: false, email: 'michael.scott@dundermifflin.com' },
    { id: 3, primary: false, email: 'mscott.scranton@gmail.com' },
];

// Richest tier, individually owned.
const license: License = {
    id: 55501,
    // Cents, which is what the API serves: every backend reader divides by 100
    // (PersonLicense::price accessor, the refund message, the renewal-invoice job).
    // A dollar figure here renders a Signature license at $4.20.
    msrp_price: 41988,
    price: 41988,
    discounts: [],
    type: 'Signature',
    expired: false,
    expires: true,
    renew: true,
    renews: demoDaysAhead(345),
    next_bill_date: demoDaysAhead(345),
    display: 'Signature',
    refundable: false,
    prorate_amount: 0,
    assigned_to: { id: DEMO_PERSON_ID, first_name: DEMO_PERSONA.first_name, last_name: DEMO_PERSONA.last_name },
    slug: LicenseType.SIGNATURE,
    remediation_type: 'sst',
    term: LicenseRenewalTerm.YEARLY,
    sponsored: false,
    sponsor_first_name: null,
    owner: { id: DEMO_PERSON_ID, type: 1, slug: 'individual', name: DEMO_PERSONA.full_name },
    scheduled_change: null,
};

/**
 * The seat the persona's record holds: his own Signature plan, or in the locked demo the Essentials
 * seat David's plan extends, as the Free licenses fixtures answer for him there.
 */
const personaSeat = (): License => (isDemoPlanLocked() ? demoSponsoredSeat() : license);

function fullOption(): PersonOption {
    return { person: true, license: true, enabled: true };
}

let personOptionsCore: PersonOptionGroups = {
    data_brokers: {
        db_people_search: fullOption(),
        db_people_search_affiliate: fullOption(),
        db_list: fullOption(),
        db_job_recruiting: fullOption(),
        db_advertising_marketing: fullOption(),
        db_background_check: fullOption(),
        db_data_enrichment: fullOption(),
        db_healthcare_insurance: fullOption(),
        db_geolocation: fullOption(),
        db_surveillance_tech: fullOption(),
        db_work_verification: fullOption(),
    },
    monitoring: {
        search_monitoring: fullOption(),
        social_media: fullOption(),
        dark_web: fullOption(),
        paste_site: fullOption(),
    },
    privacy: {
        marketing_suppression: fullOption(),
        blur_location: fullOption(),
        share_email: fullOption(),
    },
};

let communicationPreferences: PersonCommunicationPreferences = {
    monthly_privacy_report: { person: true, license: true, enabled: true },
    monthly_newsletter: { person: true, license: true, enabled: true },
    marketing_emails: { person: false, license: true, enabled: false },
    dark_web_alerts: { person: true, license: true, enabled: true },
    search_engine_exposures: { person: true, license: true, enabled: true },
    people_search_exposures: { person: true, license: true, enabled: true },
};

// A dozen marketing lists spanning every state the UI renders, so the settings tab's coverage
// summary and bulk actions all have something real to show. Coverage below is always derived
// from these rows — never hand-counted — so the two can never drift apart.
let marketingRows: MarketingOptOutRow[] = [
    { company_id: 1, name: 'Acxiom Marketing Solutions', description: 'Consumer marketing and audience data', state: 'applied', changeable: true, retired: false },
    { company_id: 2, name: 'Epsilon Data Solutions', description: 'Direct mail and email marketing lists', state: 'applied', changeable: true, retired: false },
    { company_id: 3, name: 'Experian Marketing Services', description: 'Targeted advertising audience segments', state: 'applied', changeable: true, retired: false },
    { company_id: 4, name: 'LiveRamp', description: 'Identity resolution for ad targeting', state: 'applied', changeable: true, retired: false },
    { company_id: 5, name: 'Oracle Data Cloud', description: 'Third-party ad targeting data', state: 'applied', changeable: true, retired: false },
    { company_id: 6, name: 'TransUnion Marketing Solutions', description: 'Credit-based marketing audience data', state: 'applied', changeable: true, retired: false },
    { company_id: 7, name: 'Vericast (Valassis)', description: 'Direct mail advertising lists', state: 'requested', changeable: true, retired: false },
    { company_id: 8, name: 'Merkle', description: 'Customer data platform for marketers', state: 'in_progress', changeable: true, retired: false },
    { company_id: 9, name: 'Data Axle (Infogroup)', description: 'Business and consumer marketing lists', state: 'off', changeable: true, retired: false },
    { company_id: 10, name: 'KBM Group', description: 'Loyalty and marketing data services', state: 'off', changeable: true, retired: false },
    { company_id: 11, name: 'Harte-Hanks', description: 'Direct marketing and fulfillment data', state: 'not_completed', changeable: true, retired: false },
    { company_id: 12, name: 'Wiland Inc.', description: 'Predictive marketing audiences (list retired)', state: 'applied', changeable: false, retired: true },
];

function computeCoverage(rows: MarketingOptOutRow[]): MarketingOptOutCoverage {
    const countableRows = rows.filter((r) => !r.retired);
    return {
        rows_total: rows.length,
        countable: countableRows.length,
        covered: countableRows.filter((r) => r.state === 'applied').length,
        changeable: rows.filter((r) => r.changeable).length,
        off: rows.filter((r) => r.state === 'off').length,
        requested: rows.filter((r) => r.state === 'requested').length,
        in_progress: rows.filter((r) => r.state === 'in_progress').length,
        applied: rows.filter((r) => r.state === 'applied').length,
        not_completed: rows.filter((r) => r.state === 'not_completed').length,
        locked: rows.filter((r) => !r.changeable).length,
    };
}

let passkeyIdSeq = 3;
let passkeys: Passkey[] = [
    { id: 1, device_name: 'MacBook Pro', created_at: demoDaysAgo(300), last_used_at: demoDaysAgo(1) },
    { id: 2, device_name: 'iPhone 15 Pro', created_at: demoDaysAgo(150), last_used_at: demoDaysAgo(5) },
];

let loginSessions: LoginSession[] = [
    {
        ip_address: '73.162.44.201',
        location: 'Scranton, PA',
        city: 'Scranton',
        region: 'PA',
        country: 'US',
        auth_method: 'pin',
        user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 Chrome/128.0',
        browser: 'Chrome',
        platform: 'macOS',
        first_seen_at: demoDaysAgo(0.2),
        last_seen_at: demoDaysAgo(0.01),
        event_count: 14,
        is_active: true,
        is_current: true,
        latest_token_id: 'demo-token-current',
    },
    {
        ip_address: '73.162.44.201',
        location: 'Scranton, PA',
        city: 'Scranton',
        region: 'PA',
        country: 'US',
        auth_method: 'passkey',
        user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_5 like Mac OS X) AppleWebKit/605.1.15 Safari',
        browser: 'Safari',
        platform: 'iOS',
        first_seen_at: demoDaysAgo(6),
        last_seen_at: demoDaysAgo(5),
        event_count: 5,
        is_active: false,
        is_current: false,
        latest_token_id: 'demo-token-mobile',
    },
    {
        ip_address: '98.14.201.33',
        location: 'Scranton, PA',
        city: 'Scranton',
        region: 'PA',
        country: 'US',
        auth_method: 'passkey',
        user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 Safari/605.1.15',
        browser: 'Safari',
        platform: 'macOS',
        first_seen_at: demoDaysAgo(20),
        last_seen_at: demoDaysAgo(18),
        event_count: 3,
        is_active: false,
        is_current: false,
        latest_token_id: 'demo-token-old',
    },
];

// Two AI agents, granted different capability classes, so the section shows both a read-only
// connection and one that may act on the member's behalf.
let connectedAgents: ConnectedAgent[] = [
    {
        client_id: 'a1f0c2d4-3b7e-4a19-9c62-8d5e1f0b3a47',
        name: 'chatgpt.com',
        connected_at: demoDaysAgo(23),
        last_used_at: demoDaysAgo(0.1),
        scopes: ['person.read', 'scans.write', 'offline_access'],
    },
    {
        client_id: 'c73b9e15-2f48-4d6a-b0e9-7a1c4d8f2b36',
        name: 'claude.ai',
        connected_at: demoDaysAgo(4),
        last_used_at: null,
        scopes: ['person.read'],
    },
];

let contactIdSeq = 5;
let contacts: Contact[] = [
    {
        id: 1,
        name: 'Holly Flax',
        tags: [{ name: 'Family', code: 'family' }],
        trusted_person_id: null,
        emails: [{ id: 101, email: 'holly.flax@dundermifflin.com' }],
        phone_numbers: [{ id: 201, country_code: 1, phone_number: '5705550177', phone_full: '+15705550177' }],
        blocked: false,
        public_key: null,
        risk: null,
        licensed: true,
        license: 'Essentials',
        access: true,
        is_minor: false,
        device_maps: [],
        exposures_count: 18,
        removals_count: 246,
        removals_count_30_days_ago: 221,
        in_progress_count: 5,
        in_progress_count_30_days_ago: 9,
        last_scan: demoDaysAgo(4),
        is_self: false,
    },
    {
        id: 2,
        name: 'Cornelius Scott',
        tags: [{ name: 'Family', code: 'family' }],
        trusted_person_id: null,
        emails: [],
        phone_numbers: [],
        blocked: false,
        public_key: null,
        risk: null,
        licensed: true,
        license: 'Essentials',
        access: false,
        is_minor: true,
        device_maps: [],
        exposures_count: 2,
        removals_count: 6,
        removals_count_30_days_ago: 4,
        in_progress_count: 1,
        in_progress_count_30_days_ago: 2,
        last_scan: demoDaysAgo(4),
        is_self: false,
    },
    {
        id: 3,
        name: 'Amy Scott',
        tags: [{ name: 'Family', code: 'family' }],
        trusted_person_id: null,
        emails: [],
        phone_numbers: [],
        blocked: false,
        public_key: null,
        risk: null,
        licensed: true,
        license: 'Essentials',
        access: false,
        is_minor: true,
        device_maps: [],
        exposures_count: 1,
        removals_count: 3,
        removals_count_30_days_ago: 2,
        in_progress_count: 0,
        in_progress_count_30_days_ago: 1,
        last_scan: demoDaysAgo(4),
        is_self: false,
    },
    {
        id: 4,
        name: 'Kevin Malone',
        tags: [{ name: 'Friends', code: 'friend' }],
        trusted_person_id: null,
        emails: [{ id: 102, email: 'kevin.malone@dundermifflin.com' }],
        phone_numbers: [{ id: 202, country_code: 1, phone_number: '5705550163', phone_full: '+15705550163' }],
        blocked: false,
        public_key: null,
        risk: null,
        licensed: false,
        access: false,
        is_minor: false,
        device_maps: [],
        exposures_count: 34,
        removals_count: 0,
        removals_count_30_days_ago: 0,
        in_progress_count: 0,
        in_progress_count_30_days_ago: 0,
        last_scan: null,
        is_self: false,
    },
];

let minorConsentSigned = true;

// Every US state/territory USPS uses, so the add-address dropdown never comes up empty.
const US_STATES: Array<{ name: string; code: string }> = [
    ['Alabama', 'AL'], ['Alaska', 'AK'], ['Arizona', 'AZ'], ['Arkansas', 'AR'], ['California', 'CA'],
    ['Colorado', 'CO'], ['Connecticut', 'CT'], ['Delaware', 'DE'], ['District of Columbia', 'DC'],
    ['Florida', 'FL'], ['Georgia', 'GA'], ['Hawaii', 'HI'], ['Idaho', 'ID'], ['Illinois', 'IL'],
    ['Indiana', 'IN'], ['Iowa', 'IA'], ['Kansas', 'KS'], ['Kentucky', 'KY'], ['Louisiana', 'LA'],
    ['Maine', 'ME'], ['Maryland', 'MD'], ['Massachusetts', 'MA'], ['Michigan', 'MI'], ['Minnesota', 'MN'],
    ['Mississippi', 'MS'], ['Missouri', 'MO'], ['Montana', 'MT'], ['Nebraska', 'NE'], ['Nevada', 'NV'],
    ['New Hampshire', 'NH'], ['New Jersey', 'NJ'], ['New Mexico', 'NM'], ['New York', 'NY'],
    ['North Carolina', 'NC'], ['North Dakota', 'ND'], ['Ohio', 'OH'], ['Oklahoma', 'OK'], ['Oregon', 'OR'],
    ['Pennsylvania', 'PA'], ['Rhode Island', 'RI'], ['South Carolina', 'SC'], ['South Dakota', 'SD'],
    ['Tennessee', 'TN'], ['Texas', 'TX'], ['Utah', 'UT'], ['Vermont', 'VT'], ['Virginia', 'VA'],
    ['Washington', 'WA'], ['West Virginia', 'WV'], ['Wisconsin', 'WI'], ['Wyoming', 'WY'],
].map(([name, code]) => ({ name, code }));

// ---------------------------------------------------------------------------------------------
// Builders — assembled fresh from the mutable state above on every request, so a write is always
// visible to the very next read.
// ---------------------------------------------------------------------------------------------

function buildPerson(): Person {
    const card = primaryPaymentMethod();
    const seat = personaSeat();

    return {
        id: DEMO_PERSON_ID,
        encrypted_id: 'demo-903117',
        family_id: 900001,
        employer: null,
        public_key: null,
        first_name: personCore.first_name,
        middle_name: personCore.middle_name,
        last_name: personCore.last_name,
        name: `${personCore.first_name} ${personCore.last_name}`,
        dob: personCore.dob,
        addresses,
        phone_numbers: phones,
        license: seat,
        emails,
        akas,
        pro: true,
        risk_score: DEMO_PERSONA.risk_score,
        risk_scores: {
            overall: DEMO_PERSONA.risk_score,
            data_brokers: 70,
            dark_web: 55,
            social_media: 60,
        },
        profile_complete: true,
        poa_signed: poaSigned,
        poa_envelope_id: poaSigned ? poaEnvelopeId : null,
        poa_signed_at: poaSignedAt,
        '2fa_enabled': mfaEnabled ? 1 : 0,
        has_2fa_code: mfaEnabled,
        is_minor: false,
        minor_consent_signed: minorConsentSigned,
        minor_consent_signed_at: minorConsentSigned ? demoDaysAgo(200) : undefined,
        has_family_pro: true,
        history_retention_period: '2_years',
        licenses: [seat],
        // He bought his Signature plan. The locked demo's Essentials seat is one David's plan
        // extends, which nobody bought.
        owns_purchased_license: !isDemoPlanLocked(),
        subscription: {
            subscription_status: 'active',
            next_bill_date: demoDaysAhead(345),
            card_on_file: card !== null,
            card: card
                ? { type: card.brand ?? null, last_four: card.last_four ?? null, first_name: personCore.first_name, last_name: personCore.last_name }
                : { type: null, last_four: null, first_name: null, last_name: null },
        },
        term: LicenseRenewalTerm.YEARLY,
        created_at: demoDaysAgo(480),
        // 600 removal requests and 468 scans — roughly sixteen months of work
        // Michael never had to do himself, once the one formula runs on them.
        stats: { scans: 468, removals_requested: 600 },
        last_search_presence_scan_at: demoDaysAgo(2),
        next_search_presence_scan_at: demoDaysAhead(28),
    };
}

function buildAuthUser(): AuthUser {
    const card = primaryPaymentMethod();

    return {
        identity: {
            id: DEMO_PERSON_ID,
            email: DEMO_PERSONA.email,
            // The demo persona is not addressable on the avatar host, so the
            // monogram is the honest fixture.
            avatar_url: null,
            first_name: personCore.first_name,
            last_name: personCore.last_name,
            dob: personCore.dob,
            risk_score: DEMO_PERSONA.risk_score,
            member_since: demoDaysAgo(480),
            profile_ready: true,
            lpoa_signed: true,
            lpoa_signed_at: demoDaysAgo(479),
            lpoa_envelope_id: poaEnvelopeId,
            two_factor_enabled: mfaEnabled,
            public_key: 'demo-public-key-903117',
            payment_method: card
                ? { type: card.type, last_four: card.last_four ?? '', exp_month: String(card.exp_month), exp_year: String(card.exp_year) }
                : undefined,
            location: {
                city: DEMO_PERSONA.address.city,
                region: DEMO_PERSONA.address.region,
                postal_code: DEMO_PERSONA.address.postal_code,
                country: DEMO_PERSONA.address.country,
            },
            is_pb: false,
        },
        legacy: { is_pb: false },
        employer: null,
        license: {
            id: license.id,
            name: 'signature',
            display: 'Signature',
            permissions: [
                'data_brokers.db_people_search',
                'data_brokers.db_people_search_affiliate',
                'data_brokers.db_list',
                'data_brokers.db_job_recruiting',
                'data_brokers.db_advertising_marketing',
                'data_brokers.db_background_check',
                'data_brokers.db_data_enrichment',
                'data_brokers.db_healthcare_insurance',
                'data_brokers.db_geolocation',
                'data_brokers.db_surveillance_tech',
                'data_brokers.db_work_verification',
                'monitoring.search_monitoring',
                'monitoring.social_media',
                'monitoring.dark_web',
                'monitoring.paste_site',
                'privacy.marketing_suppression',
                'privacy.blur_location',
                'privacy.share_email',
                'family_protection',
                'priority_removals',
            ],
            owner: true,
        },
        managed_accounts: [],
    };
}

function buildPersonOptions(): PersonOptions {
    return {
        ...personOptionsCore,
        license: { has_active_license: true, data_brokers_setting: null },
        coverage: computeCoverage(marketingRows),
        // The demo member already has Map Blurring on, which the server grants only once a proof
        // of address is captured for their primary address.
        requirements: { blur_location: { proof_of_address: true } },
    };
}

function setPrimaryAddress(addressId: number) {
    addresses.forEach((a) => { a.primary = a.id === addressId; });
}

function setPrimaryPhone(phoneId: number) {
    phones.forEach((p) => { p.primary = p.id === phoneId; });
}

function setPrimaryEmail(emailId: number) {
    emails.forEach((e) => { e.primary = e.id === emailId; });
}

function toPhoneFull(countryCode: unknown, phoneNumber: unknown): string {
    const cc = String(countryCode ?? '1').replace(/\D/g, '') || '1';
    const num = String(phoneNumber ?? '').replace(/\D/g, '');
    return `+${cc}${num}`;
}

export const identityRoutes: DemoRoute[] = [
    // --- Session -------------------------------------------------------------------------------
    { pattern: /^me$/, respond: () => envelope(buildAuthUser()) },

    // Closing the account only counts as done on a 204; the generic write fallback answers
    // 200, which leaves the visitor looking at an error and still signed in.
    { pattern: /^me$/, method: 'DELETE', status: 204, respond: () => envelope(null) },

    // --- MFA -------------------------------------------------------------------------------------
    { pattern: /^me\/mfa$/, respond: (): BaseAPIResponse<QRData> => envelope({ key: mfaSetupKey, qr: DEMO_QR_SVG }) },
    {
        pattern: /^me\/mfa$/,
        method: 'PATCH',
        status: 204,
        respond: () => { mfaEnabled = true; return envelope(null); },
    },
    {
        pattern: /^me\/mfa$/,
        method: 'DELETE',
        status: 204,
        respond: () => { mfaEnabled = false; return envelope(null); },
    },

    // --- Passkeys --------------------------------------------------------------------------------
    { pattern: /^me\/passkeys$/, respond: () => envelope(passkeys) },
    {
        pattern: /^me\/passkeys\/(\d+)$/,
        method: 'DELETE',
        status: 204,
        respond: (req) => {
            const passkeyId = Number(req.match[1]);
            passkeys = passkeys.filter((p) => p.id !== passkeyId);
            return envelope(null);
        },
    },
    {
        pattern: /^auth\/passkeys\/register-options$/,
        method: 'POST',
        respond: (req): BaseAPIResponse<PasskeyRegistrationOptions> => {
            const email = typeof req.body?.email === 'string' ? req.body.email : DEMO_PERSONA.email;
            return envelope({
                challenge: 'demo-challenge-903117',
                rp: { name: 'Privacy Bee', id: 'privacybee.com' },
                user: { id: `demo-user-${DEMO_PERSON_ID}`, name: email, displayName: DEMO_PERSONA.full_name },
                pubKeyCredParams: [
                    { type: 'public-key', alg: -7 },
                    { type: 'public-key', alg: -257 },
                ],
                authenticatorSelection: { authenticatorAttachment: null, userVerification: 'preferred', residentKey: 'preferred' },
                attestation: 'none',
                timeout: 60000,
                excludeCredentials: passkeys.map((p) => ({ id: `demo-cred-${p.id}`, type: 'public-key' })),
                _stateToken: 'demo-state-token',
            });
        },
    },
    {
        pattern: /^auth\/passkeys\/register$/,
        method: 'POST',
        status: 204,
        respond: (req) => {
            passkeys.push({
                id: passkeyIdSeq++,
                device_name: typeof req.body?.device_name === 'string' ? req.body.device_name : 'New Device',
                created_at: demoDaysAgo(0),
                last_used_at: null,
            });
            return envelope(null);
        },
    },

    // --- Login activity --------------------------------------------------------------------------
    { pattern: /^me\/login-activity$/, respond: () => envelope(loginSessions) },
    {
        pattern: /^me\/login-activity\/([^/]+)$/,
        method: 'DELETE',
        status: 204,
        respond: (req) => {
            const tokenId = req.match[1];
            loginSessions = loginSessions.filter((s) => s.latest_token_id !== tokenId);
            return envelope(null);
        },
    },

    // --- Connected AI agents ---------------------------------------------------------------------
    { pattern: /^me\/connections$/, respond: () => envelope(connectedAgents) },
    {
        pattern: /^me\/connections\/([^/]+)$/,
        method: 'DELETE',
        status: 204,
        respond: (req) => {
            connectedAgents = connectedAgents.filter((a) => a.client_id !== req.match[1]);
            return envelope(null);
        },
    },

    // --- LPoA ------------------------------------------------------------------------------------
    {
        pattern: /^people\/\d+\/lpoa$/,
        method: 'POST',
        status: 204,
        respond: () => { poaSigned = true; poaSignedAt = demoDaysAgo(0); return envelope(null); },
    },
    {
        pattern: /^people\/\d+\/lpoa$/,
        method: 'DELETE',
        status: 204,
        respond: () => { poaSigned = false; poaSignedAt = undefined; return envelope(null); },
    },

    // --- Countries / states (address form support) ------------------------------------------------
    // Bare, not enveloped: app-api's AddressV3APIController::getCountry returns the array itself.
    { pattern: /^countries\/[^/]+$/, respond: () => ({ areas: US_STATES }) },

    // --- Person ----------------------------------------------------------------------------------
    { pattern: /^people\/\d+$/, respond: () => envelope(buildPerson()) },
    {
        pattern: /^people\/\d+$/,
        method: 'PATCH',
        status: 201,
        respond: (req) => {
            const body = req.body ?? {};
            if (typeof body.first_name === 'string') personCore.first_name = body.first_name;
            if (typeof body.middle_name === 'string' || body.middle_name === null) personCore.middle_name = body.middle_name;
            if (typeof body.last_name === 'string') personCore.last_name = body.last_name;
            if (typeof body.dob === 'string') personCore.dob = body.dob;
            return envelope(null);
        },
    },

    // --- AKAs ------------------------------------------------------------------------------------
    {
        pattern: /^people\/\d+\/akas$/,
        method: 'POST',
        status: 201,
        respond: (req) => {
            const body = req.body ?? {};
            akas.push({
                id: akaIdSeq++,
                first_name: body.first_name ?? personCore.first_name,
                middle_name: body.middle_name ?? null,
                last_name: body.last_name ?? personCore.last_name,
            });
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/akas\/(\d+)$/,
        method: 'PATCH',
        status: 201,
        respond: (req) => {
            const akaId = Number(req.match[1]);
            const aka = akas.find((a) => a.id === akaId);
            if (aka) Object.assign(aka, req.body ?? {});
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/akas\/(\d+)$/,
        method: 'DELETE',
        status: 204,
        respond: (req) => {
            const akaId = Number(req.match[1]);
            akas = akas.filter((a) => a.id !== akaId);
            return envelope(null);
        },
    },

    // --- Addresses -------------------------------------------------------------------------------
    {
        pattern: /^people\/\d+\/addresses$/,
        method: 'POST',
        status: 201,
        respond: (req) => {
            const body = req.body ?? {};
            const id = addressIdSeq++;
            addresses.push({
                id,
                primary: false,
                line_1: body.line_1 ?? '',
                line_2: body.line_2 ?? null,
                city: body.city ?? '',
                region: body.region ?? null,
                postal_code: body.postal_code ?? '',
                country: body.country ?? 'US',
            });
            if (body.primary === true) setPrimaryAddress(id);
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/addresses\/(\d+)$/,
        method: 'PATCH',
        status: 201,
        respond: (req) => {
            const addressId = Number(req.match[1]);
            const address = addresses.find((a) => a.id === addressId);
            const body = req.body ?? {};
            if (address) Object.assign(address, body);
            if (body.primary === true) setPrimaryAddress(addressId);
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/addresses\/(\d+)$/,
        method: 'DELETE',
        status: 204,
        respond: (req) => {
            const addressId = Number(req.match[1]);
            addresses = addresses.filter((a) => a.id !== addressId);
            return envelope(null);
        },
    },

    // --- Phones ----------------------------------------------------------------------------------
    {
        pattern: /^people\/\d+\/phones\/verify$/,
        method: 'POST',
        status: 204,
        respond: () => envelope(null),
    },
    {
        pattern: /^people\/\d+\/phones$/,
        method: 'POST',
        status: 201,
        respond: (req) => {
            const body = req.body ?? {};
            phones.push({
                id: phoneIdSeq++,
                primary: false,
                country_code: String(body.country_code ?? '1'),
                phone_number: String(body.phone_number ?? ''),
                phone_full: toPhoneFull(body.country_code, body.phone_number),
            });
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/phones\/(\d+)$/,
        method: 'PATCH',
        respond: (req) => {
            const phoneId = Number(req.match[1]);
            const body = req.body ?? {};
            if (body.primary === true) {
                setPrimaryPhone(phoneId);
            } else {
                const phone = phones.find((p) => p.id === phoneId);
                if (phone) Object.assign(phone, body);
            }
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/phones\/(\d+)$/,
        method: 'DELETE',
        status: 204,
        respond: (req) => {
            const phoneId = Number(req.match[1]);
            phones = phones.filter((p) => p.id !== phoneId);
            return envelope(null);
        },
    },

    // --- Emails ----------------------------------------------------------------------------------
    {
        pattern: /^me\/emails\/verify$/,
        method: 'POST',
        status: 204,
        respond: () => envelope(null),
    },
    {
        pattern: /^people\/\d+\/emails$/,
        method: 'POST',
        status: 201,
        respond: (req) => {
            const body = req.body ?? {};
            const id = emailIdSeq++;
            emails.push({ id, primary: false, email: body.email ?? `demo-${id}@dundermifflin.com` });
            if (body.primary === true) setPrimaryEmail(id);
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/emails\/(\d+)$/,
        method: 'PATCH',
        respond: (req) => {
            const emailId = Number(req.match[1]);
            if (req.body?.primary === true) setPrimaryEmail(emailId);
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/emails\/(\d+)$/,
        method: 'DELETE',
        respond: (req) => {
            const emailId = Number(req.match[1]);
            emails = emails.filter((e) => e.id !== emailId);
            return envelope(null);
        },
    },

    // --- Protection options ------------------------------------------------------------------------
    { pattern: /^people\/\d+\/options$/, respond: () => envelope(buildPersonOptions()) },
    {
        pattern: /^people\/\d+\/options$/,
        method: 'PATCH',
        status: 201,
        respond: (req) => {
            const body = (req.body ?? {}) as PersonOptionsUpdatePayload;
            (['data_brokers', 'monitoring', 'privacy'] as const).forEach((section) => {
                const patch = body[section];
                if (!patch) return;
                Object.assign(personOptionsCore[section], patch);
            });
            return envelope(null);
        },
    },

    // --- Marketing opt-outs ------------------------------------------------------------------------
    {
        pattern: /^people\/\d+\/marketing-optouts$/,
        respond: () => envelope({ rows: marketingRows, coverage: computeCoverage(marketingRows) }),
    },
    {
        pattern: /^people\/\d+\/marketing-optouts$/,
        method: 'PATCH',
        status: 201,
        respond: (req) => {
            const body = (req.body ?? {}) as MarketingOptOutsUpdatePayload;
            const targets = new Set(body.company_ids ?? []);
            const results: MarketingOptOutResult[] = [];

            marketingRows.forEach((row) => {
                if (!targets.has(row.company_id) || !row.changeable) return;
                row.state = body.on ? 'applied' : 'off';
                results.push({
                    company_id: row.company_id,
                    outcome: body.on ? 'submitted' : 'withdrawn',
                    refused: false,
                    state: row.state,
                    changeable: row.changeable,
                });
            });

            return envelope({ results, coverage: computeCoverage(marketingRows) });
        },
    },

    // --- Communication preferences -----------------------------------------------------------------
    { pattern: /^people\/\d+\/communication-preferences$/, respond: () => envelope(communicationPreferences) },
    {
        pattern: /^people\/\d+\/communication-preferences$/,
        method: 'PATCH',
        status: 201,
        respond: (req) => {
            const patch = (req.body ?? {}) as PersonCommunicationPreferencesUpdatePayload;
            (Object.keys(patch) as CommunicationPreferenceKey[]).forEach((key) => {
                const value = patch[key];
                if (!value) return;
                const current = communicationPreferences[key];
                communicationPreferences[key] = { ...current, person: value.person, enabled: value.person && current.license };
            });
            return envelope(communicationPreferences);
        },
    },

    // --- Contacts / family -------------------------------------------------------------------------
    {
        pattern: /^people\/\d+\/contacts$/,
        respond: (req) => {
            const includeSelf = req.query.get('include_self') === 'true';
            if (!includeSelf) return envelope(contacts);

            const self: Contact = {
                id: DEMO_PERSON_ID,
                name: `${personCore.first_name} ${personCore.last_name}`,
                tags: [],
                trusted_person_id: null,
                emails: emails.map((e): ContactEmail => ({ id: e.id, email: e.email })),
                phone_numbers: phones.map((p): ContactPhoneNumber => ({
                    id: p.id,
                    country_code: Number(p.country_code) || 1,
                    phone_number: p.phone_number,
                    phone_full: p.phone_full,
                })),
                blocked: false,
                public_key: null,
                risk: null,
                licensed: true,
                license: 'Signature',
                access: true,
                is_minor: false,
                device_maps: [],
                exposures_count: 3264,
                removals_count: 7431,
                removals_count_30_days_ago: 7118,
                in_progress_count: 1284,
                in_progress_count_30_days_ago: 15,
                last_scan: demoDaysAgo(1),
                is_self: true,
            };

            return { ...envelope(contacts), self };
        },
    },
    {
        pattern: /^people\/\d+\/contacts$/,
        method: 'POST',
        status: 201,
        respond: (req) => {
            const entry = Array.isArray(req.body) ? req.body[0] : req.body;
            const rawEmail = Array.isArray(entry?.emails) ? entry.emails[0] : entry?.email;
            contacts.push({
                id: contactIdSeq++,
                name: entry?.name ?? 'New Contact',
                tags: [{ name: 'Friends', code: 'friend' }],
                trusted_person_id: null,
                emails: rawEmail ? [{ id: contactIdSeq * 100, email: String(rawEmail) }] : [],
                phone_numbers: (Array.isArray(entry?.phone_numbers) ? entry.phone_numbers : []).map(
                    (phone: { country_code: number | string; phone_number: string }, index: number) => ({
                        id: contactIdSeq * 100 + index + 1,
                        country_code: Number(phone.country_code),
                        phone_number: String(phone.phone_number),
                        phone_full: `+${phone.country_code}${phone.phone_number}`,
                    }),
                ),
                blocked: false,
                public_key: null,
                risk: null,
                licensed: false,
                access: false,
                is_minor: false,
                device_maps: [],
                exposures_count: 0,
                removals_count: 0,
                removals_count_30_days_ago: 0,
                in_progress_count: 0,
                in_progress_count_30_days_ago: 0,
                last_scan: null,
                is_self: false,
            });
            return envelope(null);
        },
    },
    {
        pattern: /^people\/\d+\/contacts\/minor-eligibility$/,
        respond: (): MinorEligibility => ({ eligible: true, consent_signed: minorConsentSigned, agreement_variant: 'standard' }),
    },
    {
        pattern: /^people\/\d+\/contacts\/minor-consent$/,
        method: 'POST',
        status: 204,
        respond: () => { minorConsentSigned = true; return envelope(null); },
    },
    {
        pattern: /^people\/\d+\/contacts\/minor$/,
        method: 'POST',
        status: 201,
        respond: (req) => {
            const body = req.body ?? {};
            const contact: Contact = {
                id: contactIdSeq++,
                name: `${body.first_name ?? 'New'} ${body.last_name ?? 'Child'}`.trim(),
                tags: [{ name: 'Family', code: 'family' }],
                trusted_person_id: null,
                emails: [],
                phone_numbers: [],
                blocked: false,
                public_key: null,
                risk: null,
                licensed: true,
                license: 'Essentials',
                access: false,
                is_minor: true,
                device_maps: [],
                exposures_count: 0,
                removals_count: 0,
                removals_count_30_days_ago: 0,
                in_progress_count: 0,
                in_progress_count_30_days_ago: 0,
                last_scan: null,
                is_self: false,
            };
            contacts.push(contact);
            return envelope(contact);
        },
    },
    {
        // Completing a member replaces its addresses and numbers and answers with the member as it
        // now stands. Without this the unmatched write falls through to `{ data: null }`, which a
        // caller reads as the member itself.
        pattern: /^people\/\d+\/contacts\/(\d+)$/,
        method: 'PUT',
        status: 200,
        respond: (req) => {
            const contactId = Number(req.match[1]);
            const body = req.body as {
                name?: string;
                emails?: string[];
                phone_numbers?: Array<{ country_code: number | string; phone_number: string }>;
            };
            const existing = contacts.find((c) => c.id === contactId);
            if (!existing) return envelope(null);

            existing.name = body?.name ?? existing.name;
            existing.emails = (body?.emails ?? []).map((email, index) => ({ id: contactId * 100 + index + 1, email }));
            existing.phone_numbers = (body?.phone_numbers ?? []).map((phone, index) => ({
                id: contactId * 1000 + index + 1,
                country_code: Number(phone.country_code),
                phone_number: String(phone.phone_number),
                phone_full: `+${phone.country_code}${phone.phone_number}`,
            }));

            return envelope(existing);
        },
    },
    {
        pattern: /^people\/\d+\/contacts\/(\d+)$/,
        method: 'DELETE',
        status: 204,
        respond: (req) => {
            const contactId = Number(req.match[1]);
            contacts = contacts.filter((c) => c.id !== contactId);
            return envelope(null);
        },
    },
];
